import { Injectable } from '@angular/core';
import { CustomXmlPart } from '../interfaces/customxmlpart';
import { AsyncResult } from '../utils/asyncresult';

@Injectable({ providedIn: 'root' })
export class WordService {
  private static readonly CONTENT_CONTROL_PROPERTIES = ['id', 'tag', 'title', 'parentContentControlOrNullObject'];

  constructor() {}

  getCustomXmlPart(id: string): Promise<CustomXmlPart> {
    return new Promise((resolve, reject) => {
      Office.context.document.customXmlParts.getByIdAsync(id, (result) => {
        if (!AsyncResult.succeeded(result)) {
          WordService.reject(`Could not find part ${id}. ${result.error}`, reject);
        } else {
          resolve(new CustomXmlPart(result.value));
        }
      });
    });
  }

  createCustomXmlPart(xml: string): Promise<CustomXmlPart> {
    return new Promise((resolve, reject) => {
      Office.context.document.customXmlParts.addAsync(xml, (result) => {
        if (!AsyncResult.succeeded(result)) {
          WordService.reject(`Failed to create custom part. ${result.error.message ?? result.error}`, reject);
          return;
        }

        resolve(new CustomXmlPart(result.value));
      });
    });
  }

  async getContentControl(context: Word.RequestContext, id: number): Promise<Word.ContentControl | undefined> {
    const cc = context.document.contentControls.getByIdOrNullObject(id);
    cc.load(WordService.CONTENT_CONTROL_PROPERTIES);
    await context.sync();

    if (cc.isNullObject) {
      return undefined;
    }

    return cc;
  }

  /**
   * Gets the containers of the supplied content control (inclusive) ordered from innermost (self) to outermost container.
   * @param context the RequestContext used to access Word object model
   * @param id the id of the contained (innermost) content control
   * @returns all containers AND the supplied contained content control
   */
  async getContainers(context: Word.RequestContext, id: number): Promise<{ id: number; tag: string }[]> {
    const allContentControls = await this.loadContentControls(context);
    let lookup: number = id;
    const results = [];
    while (true) {
      const cc = allContentControls.find((wcc) => wcc.id === lookup);
      if (!cc) {
        break;
      }

      results.push({ id: cc.id, tag: cc.tag });

      if (cc.parentContentControlOrNullObject.isNullObject) {
        break;
      }

      lookup = cc.parentContentControlOrNullObject.id;
    }
    return results;
  }

  /**
   * Loads all the content controls in the document. Includes the content control id, tag, title and parentOrNullObject.
   * @param context the RequestContext used to access Word object model
   */
  async loadContentControls(context: Word.RequestContext): Promise<Word.ContentControl[]> {
    const contentControls = context.document.contentControls.load(WordService.CONTENT_CONTROL_PROPERTIES);
    await context.sync();
    return contentControls.items;
  }

  private static reject(msg: string, reject: (reason?: any) => void): void {
    console.error(msg);
    reject(msg);
  }
}
